import * as React from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import clsx from 'clsx';
import { useRouter } from 'next/router';

import { GuestSelectModal } from 'components/MultiProductDateForm/GuestSelectModal';
import { MultiProductDatePicker } from 'components/MultiProductDateForm/MultiProductDatePicker';
import { ReduxState } from 'ducks';
import { useBounds } from 'hooks/useBounds';
import { getBookingStartDateTimeText } from 'lib/util/getBookingStartDateTimeText';
import { isStillNotOnSale as checkStillNotOnSale } from 'lib/util/isStillNotOnSale';

import { RequestIcon } from './RequestIcon';
import { InstantIcon } from './InstantIcon';
import { useUrl } from 'hooks/useUrl';

import styles from './MultiProductDateForm.module.css';
import {
  attrMapHasFullyBookedDate,
  attrMapHasPromotionDate,
  getAttrByDateMap,
} from 'lib/util/datePickerHelpers';

const postHeightChangedMessage = (height: number) => {
  window.parent.postMessage(
    {
      type: 'HEIGHT_CHANGED',
      height,
    },
    '*'
  );
};

const postOpenGuestSelectModalMessage = (url: string) => {
  window.parent.postMessage(
    {
      type: 'OPEN_GUEST_SELECT_MODAL',
      url,
    },
    '*'
  );
};

export const MultiProductDateForm = () => {
  const productSummaries = useSelector((state: ReduxState) => state.server.productSummaries.all);

  const router = useRouter();

  const initialSelectedProductId = productSummaries.length > 0 ? productSummaries[0].id : '';

  const [date, setDate] = React.useState('');
  const [showModal, setShowModal] = React.useState(false);
  const [selectedProductId, setSelectedProductId] = React.useState<string>(
    initialSelectedProductId
  );
  const [dateFormRef, dateFormBounds] = useBounds<HTMLDivElement>();

  const { t } = useTranslation();

  React.useEffect(() => {
    if (productSummaries.length > 0) {
      setSelectedProductId(productSummaries[0].id ?? '');
    }
  }, [productSummaries]);
  React.useEffect(() => {
    postHeightChangedMessage(dateFormBounds.height);
  }, [dateFormBounds.height]);

  const handleProductChange = (event: React.ChangeEvent<HTMLSelectElement>) =>
    setSelectedProductId(event.target.value);

  const productInstances = useSelector((state: ReduxState) => state.productInstances.all);
  const selectedProduct = productSummaries.find((product) => product.id === selectedProductId);

  const baseUrl = useUrl(`/products/${selectedProductId}/selectGuestsAndTime`);

  const isModal = Boolean(router?.query.isModal);

  const attrMap = getAttrByDateMap(productInstances, selectedProduct?.start_timezone ?? '', 0);

  const handleDateClick = (date: string) => {
    let embeddedUrl = `/embed/${baseUrl}`;

    if (embeddedUrl.includes('?')) {
      embeddedUrl = `${embeddedUrl}&date=${date}`;
    } else {
      embeddedUrl = `${embeddedUrl}?date=${date}`;
    }

    const isDisplayingInIframe =
      typeof window !== 'undefined' && window.location !== window.parent.location;

    if (isModal) {
      setDate(date);
      setShowModal(true);
    } else if (isDisplayingInIframe) {
      postOpenGuestSelectModalMessage(`${window.location.origin}${embeddedUrl}`);
    } else {
      router.replace(embeddedUrl);
    }
  };

  if (productSummaries.length === 0) {
    return null;
  }

  const postCloseModalMessage = () => {
    window.parent.postMessage(
      {
        type: 'CLOSE_MODAL',
      },
      '*'
    );
  };

  const onClose = () => {
    const isDisplayingInIframe =
      typeof window !== 'undefined' && window.location !== window.parent.location;
    if (isDisplayingInIframe) {
      postCloseModalMessage();
    } else {
      router.back();
    }
  };

  const isNotOnSale = Boolean(
    !selectedProduct?.booking_widget_settings?.is_visible ||
      selectedProduct?.booking_widget_settings?.is_view_only
  );

  const isStillNotOnSale = checkStillNotOnSale(selectedProduct);

  const hasPromotionDate = attrMapHasPromotionDate(attrMap);
  const hasFullyBookedDate = attrMapHasFullyBookedDate(attrMap);

  return (
    <>
      <div className="widget-multi-calendar--frame">
        <div ref={dateFormRef} className="widget-multi-calendar">
          {showModal && <div className={styles['dimmer']} />}
          <div className="widget-multi-calendar__header">
            {t('Make a booking')}
            {isModal && (
              <p className="widget-multi-calendar__header__close" onClick={onClose}>
                <span />
              </p>
            )}
          </div>
          <div className="widget-multi-calendar__body">
            <div className="widget-multi-calendar__body__tour">
              <p className="widget-multi-calendar__body__tour__title">{t('Select Plan')}</p>
              <label className="widget-multi-calendar__body__tour__select">
                <select
                  className="widget-multi-calendar__body__tour__select__item"
                  value={selectedProductId}
                  onChange={handleProductChange}
                >
                  {productSummaries.map((product, idx) => (
                    <option key={idx} value={product.id}>
                      {product.product_name}
                    </option>
                  ))}
                </select>
              </label>
            </div>
            {(isNotOnSale || isStillNotOnSale) && (
              <div
                className="widget-multi-calendar__body__tour"
                style={{ borderBottom: 'none', backgroundColor: 'white' }}
              >
                <p className="widget-multi-calendar__body__tour__title" style={{ color: 'red' }}>
                  {isNotOnSale
                    ? t('Sorry, this product is currently not available.')
                    : getBookingStartDateTimeText(selectedProduct, t)}
                </p>
              </div>
            )}
            {!isNotOnSale && !isStillNotOnSale && (
              <>
                <MultiProductDatePicker product={selectedProduct} onDateClick={handleDateClick} />
                <ul className="widget-multi-calendar__body__comment">
                  <li className="widget-multi-calendar__body__comment__item">
                    <p className="widget-multi-calendar__body__comment__item__ic">
                      <InstantIcon />
                    </p>
                    <p className="widget-multi-calendar__body__comment__item__name">
                      {' '}
                      : {t('Instant')}
                    </p>
                    <p className="widget-multi-calendar__body__comment__item__hatena">?</p>
                    <p className="widget-multi-calendar__body__comment__item__info">
                      {t(
                        'Instant bookings will be confirmed automatically within seconds of submission'
                      )}
                    </p>
                  </li>
                  <li className="widget-multi-calendar__body__comment__item">
                    <p className="widget-multi-calendar__body__comment__item__ic">
                      <RequestIcon />
                    </p>
                    <p className="widget-multi-calendar__body__comment__item__name">
                      {' '}
                      : {t('Request')}
                    </p>
                    <p className="widget-multi-calendar__body__comment__item__hatena">?</p>
                    <p className="widget-multi-calendar__body__comment__item__info">
                      {t('Request-only bookings require explicit confirmation from supplier')}
                    </p>
                  </li>
                  {hasFullyBookedDate && (
                    <li className="widget-multi-calendar__body__comment__item">
                      <p className="widget-multi-calendar__body__comment__item__ic">
                        <img src="/static/images/ic_fullybooked.svg" alt="closed" />
                      </p>
                      <p className="widget-multi-calendar__body__comment__item__name">
                        {' '}
                        : {t('Fully Booked')}
                      </p>
                    </li>
                  )}
                  {hasPromotionDate && (
                    <li className="widget-multi-calendar__body__comment__item">
                      <p
                        className={clsx('widget-multi-calendar__body__comment__item__ic', 'promo')}
                      >
                        <img src="/static/images/ic_promo.svg" />
                      </p>
                      <p className="widget-multi-calendar__body__comment__item__name">
                        {' '}
                        : {t('Campaign')}
                      </p>
                      <p className="widget-multi-calendar__body__comment__item__hatena">?</p>
                      <p className="widget-multi-calendar__body__comment__item__info">
                        {t('One or more campaigns apply to bookings on this date')}
                      </p>
                    </li>
                  )}

                  <li className="widget-multi-calendar__body__comment__item">
                    <p className="widget-multi-calendar__body__comment__item__ic">
                      <img src="/static/images/ic_ng.svg" alt="closed" />
                    </p>
                    <p className="widget-multi-calendar__body__comment__item__name">
                      {' '}
                      : {t('Closed')}
                    </p>
                  </li>
                </ul>
              </>
            )}
          </div>
        </div>
      </div>
      {showModal && (
        <GuestSelectModal
          product={selectedProduct ?? null}
          date={date}
          onClose={() => setShowModal(false)}
        />
      )}
    </>
  );
};
